import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { initAdminFcm } from '../services/fcmService';

interface Tenant {
  name: string;
  code: string;
}

interface User {
  _id: string;
  name: string;
  mobile: string;
  role: string;
  tenantId?: string;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  login: (mobile: string, password: string, tenantCode?: string) => Promise<void>;
  superAdminLogin: (mobile: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        if (storedTenant) {
          setTenant(JSON.parse(storedTenant));
        }
        authService.setToken(storedToken);
      } catch (error) {
        // Clear invalid data from localStorage
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    if (user.role === 'super_admin') return;

    initAdminFcm();
  }, [token, user]);

  const login = async (mobile: string, password: string, tenantCode?: string) => {
    const response = await authService.login(mobile, password, tenantCode);
    setToken(response.token);
    setUser(response.user);
    setTenant(response.tenant || null);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    if (response.tenant) {
      localStorage.setItem('tenant', JSON.stringify(response.tenant));
    }
  };

  const superAdminLogin = async (mobile: string, password: string) => {
    const response = await authService.superAdminLogin(mobile, password);
    setToken(response.token);
    setUser(response.user);
    setTenant(null);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.removeItem('tenant');
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setTenant(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    authService.setToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, token, login, superAdminLogin, logout, isLoading, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

